import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";

const ST = { add: ["تسجيل كآخر إجازة", "bg-emerald-50 text-emerald-700"], keep: ["إجازة النظام أحدث", "bg-slate-100 text-slate-600"], not_found: ["غير موجود في النظام", "bg-amber-50 text-amber-700"] };

export const MuqeemLeaveImport = ({ onDone }) => {
  const ref = useRef(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try { setData(await api.muqeemLeavePreview(f)); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر قراءة الملف"); } finally { setBusy(false); }
  };
  const adds = (data?.rows || []).filter((r) => r.status === "add");
  const apply = async () => {
    setBusy(true);
    try {
      const items = (data?.rows || []).filter((x) => x.employee_id).map((x) => ({ employee_id: x.employee_id, exit_date: x.exit_date, visa_expiry: x.visa_expiry || "", duration_days: x.duration_days ?? null, visa_no: x.visa_no || "", return_date: x.return_date || "" }));
      const r = await api.muqeemLeaveApply(items);
      toast.success(`تم تسجيل آخر إجازة لـ ${r.added} موظف ✅`); setData(null); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const count = (s) => (data?.rows || []).filter((r) => r.status === s).length;
  return (
    <>
      <input ref={ref} type="file" hidden accept=".xlsx,.xls" data-testid="muqeem-leave-input" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      <Button type="button" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="muqeem-leave-import-btn" className="gap-1.5 border-teal-200 text-teal-700">
        {busy && !data ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} رفع التأشيرات (مقيم)
      </Button>
      <Dialog open={!!data} onOpenChange={(v) => { if (!v && !busy) setData(null); }}>
        <DialogContent className="max-w-3xl w-[96vw] max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="muqeem-leave-dialog">
          <DialogHeader className="text-right">
            <DialogTitle>استيراد آخر إجازة من مقيم</DialogTitle>
            <DialogDescription>آخر خروج لكل موظف من «تاريخ إصدار التأشيرة بالميلادي». الإجازة القادمة = تاريخ الخروج + {data?.gap_months} شهرًا (من إعدادات الشركة). يُحتفظ دائماً بالأحدث.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 text-xs" data-testid="muqeem-leave-counts">
            {Object.keys(ST).map((k) => <span key={k} className={`rounded-full px-2.5 py-1 font-semibold ${ST[k][1]}`}>{ST[k][0]}: {count(k)}</span>)}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-right text-xs">
              <thead className="bg-slate-50 text-slate-500"><tr>{["الموظف", "رقم الإقامة", "تاريخ الخروج", "آخر إجازة بالنظام", "الإجازة القادمة", "الحالة"].map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
              <tbody>
                {(data?.rows || []).map((r) => (
                  <tr key={r.iqama} className="border-b" data-testid={`muqeem-row-${r.iqama}`}>
                    <td className="p-2 font-semibold">{r.employee_name || r.name}</td>
                    <td className="p-2 font-mono">{r.iqama}</td>
                    <td className="p-2 font-mono">{r.exit_date}</td>
                    <td className="p-2 font-mono">{r.current_last || "—"}</td>
                    <td className="p-2 font-mono font-bold text-teal-700">{r.next_eligible || "—"}</td>
                    <td className="p-2"><span className={`rounded-full px-2 py-0.5 ${ST[r.status][1]}`}>{ST[r.status][0]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end pt-2">
            <Button disabled={!(data?.rows || []).some((x) => x.employee_id) || busy} onClick={apply} data-testid="muqeem-leave-apply" className="gap-1.5 bg-teal-600 hover:bg-teal-700">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ {adds.length} إجازة
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
